import React, { useState, useEffect } from 'react';
import {
  FiRefreshCw,
  FiSearch,
  FiEye,
  FiDollarSign,
  FiX,
  FiCheck
} from 'react-icons/fi';
import AdminService from '../services/AdminService';

const PartnerVerifications = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // 'view' | 'review' | 'commission'
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewAction, setReviewAction] = useState('APPROVE');
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Commission Configuration State
  const [commissionConfig, setCommissionConfig] = useState({
    commissionType: 'PERCENTAGE',
    commissionValue: 10,
  });
  const [savingComm, setSavingComm] = useState(false);

  const loadCommission = async () => {
    try {
      const res = await AdminService.getCommissionConfig();
      if (res && res.config) {
        setCommissionConfig({
          commissionType: res.config.commissionType || 'PERCENTAGE',
          commissionValue: res.config.commissionValue ?? 10,
        });
      }
    } catch (err) {
      console.error('Error loading commission config:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadCommission();
      const res = await AdminService.getPartnerVerifications({
        search,
        status: statusFilter,
      });
      if (res && res.verifications) {
        setList(res.verifications);
      }
    } catch (err) {
      console.error('Error fetching partner verifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const handleSaveCommission = async (e) => {
    e.preventDefault();
    setSavingComm(true);
    try {
      const res = await AdminService.updateCommissionConfig({
        commissionType: commissionConfig.commissionType,
        commissionValue: Number(commissionConfig.commissionValue),
        scope: 'GLOBAL',
      });
      if (res && res.success) {
        alert(res.message);
        setModal(null);
        loadData();
      }
    } catch (err) {
      alert('Error updating commission: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingComm(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadData();
  };

  // Per-partner custom commission state in review modal
  const [partnerCommType, setPartnerCommType] = useState('PERCENTAGE');
  const [partnerCommVal, setPartnerCommVal] = useState(0);

  const openReviewModal = (app, actionType) => {
    setSelectedApp(app);
    setReviewAction(actionType);
    setReason('');
    setPartnerCommType(app.customCommission?.commissionType || 'PERCENTAGE');
    setPartnerCommVal(app.customCommission?.commissionValue ?? 0);
    setModal('review');
  };

  const handleReviewSubmit = async () => {
    if (!selectedApp) return;
    setActionLoading(true);
    try {
      const res = await AdminService.reviewPartnerVerification(selectedApp._id, {
        action: reviewAction,
        reason,
        commissionType: partnerCommType,
        commissionValue: Number(partnerCommVal),
      });
      if (res && res.success) {
        alert(`Partner application updated to ${res.verification?.status}! Custom commission set to ${partnerCommVal}${partnerCommType === 'PERCENTAGE' ? '%' : ' ₹'}`);
        setModal(null);
        loadData();
      }
    } catch (err) {
      alert('Failed to review application: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openEditPartnerCommModal = (app) => {
    setSelectedApp(app);
    setPartnerCommType(app.customCommission?.commissionType || 'PERCENTAGE');
    setPartnerCommVal(app.customCommission?.commissionValue ?? 0);
    setModal('editPartnerComm');
  };

  const handleSavePartnerCommDirectly = async (e) => {
    e.preventDefault();
    if (!selectedApp) return;
    setSavingComm(true);
    try {
      const pId = selectedApp.partnerId?._id || selectedApp.partnerId;
      const res = await AdminService.setPartnerCommission(pId, partnerCommType, Number(partnerCommVal));
      if (res && res.success) {
        alert(res.message);
        setModal(null);
        loadData();
      }
    } catch (err) {
      alert('Failed to set partner commission: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingComm(false);
    }
  };

  const handleTriggerSettlement = async (partnerId) => {
    if (!window.confirm('Trigger Razorpay payout/settlement for this partner available balance?')) return;
    try {
      const res = await AdminService.triggerPartnerSettlement(partnerId);
      if (res && res.success) {
        alert(res.message);
        loadData();
      }
    } catch (err) {
      alert('Settlement trigger error: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="space-y-6 max-w-6xl text-left pb-20">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Partner Verifications & Payouts</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Review partner KYC applications & manage automated Razorpay Route linked accounts</p>
        </div>

        <button
          onClick={loadData}
          className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-bold transition flex items-center gap-2 shadow-xs self-start sm:self-auto"
        >
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filter & Search Controls */}
      <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
        <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <FiSearch className="absolute left-3.5 top-3 text-slate-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search legal name, PAN, mobile..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
            />
          </div>
          <button type="submit" className="px-4 py-2.5 bg-purple-700 text-white rounded-xl text-xs font-bold hover:bg-purple-800 transition shadow-xs">
            Search
          </button>
        </form>

        <div className="flex gap-2 w-full md:w-auto">
          {['', 'SUBMITTED', 'APPROVED', 'RAZORPAY_ACTIVE', 'REJECTED', 'RESUBMISSION_REQUIRED'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                statusFilter === st ? 'bg-purple-700 text-white shadow-xs' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {st || 'All Statuses'}
            </button>
          ))}
        </div>
      </div>

      {/* VERIFICATION QUEUE TABLE */}
      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-medium text-xs">
            No partner verification requests found matching filter criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase text-[10px] font-black tracking-wider">
                  <th className="px-5 py-3.5">Partner Legal Name</th>
                  <th className="px-5 py-3.5">Commission Rate</th>
                  <th className="px-5 py-3.5">PAN & GST</th>
                  <th className="px-5 py-3.5">Bank Details</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((item) => (
                  <tr key={item._id} className="hover:bg-slate-50/70 transition">
                    <td className="px-5 py-4">
                      <div>
                        <span className="font-extrabold text-slate-900 block">{item.legalName}</span>
                        {item.organizationName && (
                          <span className="text-[11px] font-medium text-slate-500 block">{item.organizationName}</span>
                        )}
                        <span className="text-[10px] text-slate-400 block">{item.email} • {item.mobileNumber}</span>
                      </div>
                    </td>

                    <td className="px-5 py-4 font-semibold text-slate-700">
                      <span className={`px-2.5 py-1 rounded-xl text-[11px] font-black border ${
                        item.customCommission && item.customCommission.commissionValue === 0
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : 'bg-purple-50 text-purple-900 border-purple-200'
                      }`}>
                        {item.customCommission
                          ? (item.customCommission.commissionValue === 0
                              ? '0% Fee'
                              : (item.customCommission.commissionType === 'FIXED'
                                  ? `₹${item.customCommission.commissionValue} (Fixed)`
                                  : `${item.customCommission.commissionValue}%`))
                          : '0% Fee'}
                      </span>
                    </td>

                    <td className="px-5 py-4 font-mono">
                      <span className="font-bold text-slate-800 block">PAN: {item.pan}</span>
                      {item.gstin && <span className="text-[11px] text-slate-500 block">GST: {item.gstin}</span>}
                    </td>

                    <td className="px-5 py-4">
                      <span className="font-extrabold text-purple-900 block font-mono">{item.bankDetails?.maskedAccountNumber || 'XXXX XXXX 1234'}</span>
                      <span className="text-[10px] font-semibold text-slate-500 uppercase block">IFSC: {item.bankDetails?.ifscCode || '-'}</span>
                    </td>

                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border ${
                        item.status === 'APPROVED' || item.status === 'RAZORPAY_ACTIVE'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : (item.status === 'REJECTED' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200')
                      }`}>
                        {item.status}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-right space-x-2">
                      <button
                        onClick={() => { setSelectedApp(item); setModal('view'); }}
                        className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition inline-flex items-center gap-1 shadow-xs"
                      >
                        <FiEye className="w-3.5 h-3.5" /> View
                      </button>

                      <button
                        onClick={() => openEditPartnerCommModal(item)}
                        className="px-3 py-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-900 text-xs font-bold transition inline-flex items-center gap-1 shadow-xs"
                      >
                        <FiDollarSign className="w-3.5 h-3.5 text-purple-700" /> Commission
                      </button>

                      {['SUBMITTED', 'UNDER_REVIEW'].includes(item.status) && (
                        <button
                          onClick={() => openReviewModal(item, 'APPROVE')}
                          className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition inline-flex items-center gap-1 shadow-xs"
                        >
                          <FiCheck className="w-3.5 h-3.5" /> Approve
                        </button>
                      )}

                      {['SUBMITTED', 'UNDER_REVIEW'].includes(item.status) && (
                        <button
                          onClick={() => openReviewModal(item, 'REQUEST_RESUBMISSION')}
                          className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition inline-flex items-center gap-1 shadow-xs"
                        >
                          Resubmit
                        </button>
                      )}

                      {item.status === 'RAZORPAY_ACTIVE' && (
                        <button
                          onClick={() => handleTriggerSettlement(item.partnerId?._id || item.partnerId)}
                          className="px-3 py-1.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition inline-flex items-center gap-1 shadow-xs"
                        >
                          <FiDollarSign className="w-3.5 h-3.5" /> Settle Payout
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 1. VIEW VERIFICATION DETAILS MODAL */}
      {modal === 'view' && selectedApp && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 w-full max-w-2xl space-y-6 text-left shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <h3 className="text-base font-black text-slate-900">Partner Verification & KYC Details</h3>
                <span className="text-xs text-slate-500 font-medium">Submitted on {new Date(selectedApp.createdAt).toLocaleDateString('en-IN')}</span>
              </div>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Legal Name</span>
                <span className="font-extrabold text-slate-900">{selectedApp.legalName}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Organization Name</span>
                <span className="font-extrabold text-slate-900">{selectedApp.organizationName || '-'}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">PAN Number</span>
                <span className="font-extrabold text-purple-900 font-mono">{selectedApp.pan}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">GSTIN</span>
                <span className="font-extrabold text-slate-800 font-mono">{selectedApp.gstin || 'Not Provided'}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Bank Account Holder</span>
                <span className="font-extrabold text-slate-900">{selectedApp.bankDetails?.accountHolderName}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Masked Account Number</span>
                <span className="font-extrabold text-slate-900 font-mono">{selectedApp.bankDetails?.maskedAccountNumber || 'XXXX XXXX 1234'}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">IFSC Code</span>
                <span className="font-extrabold text-purple-900 font-mono uppercase">{selectedApp.bankDetails?.ifscCode}</span>
              </div>

              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Address</span>
                <span className="font-semibold text-slate-800">{selectedApp.address}, {selectedApp.city}, {selectedApp.state} - {selectedApp.pinCode}</span>
              </div>
            </div>

            {/* AUDIT LOG TIMELINE */}
            {selectedApp.reviewHistory && selectedApp.reviewHistory.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Audit Log Timeline</h4>
                <div className="space-y-1.5">
                  {selectedApp.reviewHistory.map((h, idx) => (
                    <div key={idx} className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs flex justify-between items-center">
                      <div>
                        <span className="font-bold text-purple-900 block">{h.action}</span>
                        <span className="text-[11px] text-slate-600">{h.reason}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">{new Date(h.timestamp).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. REVIEW MODAL (APPROVE / REJECT / RESUBMIT) WITH CUSTOM COMMISSION INPUT */}
      {modal === 'review' && selectedApp && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-md space-y-4 text-left shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-black text-slate-900">Review Partner Verification</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Select Review Action</label>
                <select
                  value={reviewAction}
                  onChange={e => setReviewAction(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                >
                  <option value="APPROVE">Approve & Enable Razorpay Linked Account</option>
                  <option value="REQUEST_RESUBMISSION">Request Resubmission (Correction Needed)</option>
                  <option value="REJECT">Reject Application</option>
                  <option value="SUSPEND">Suspend Partner Payouts</option>
                </select>
              </div>

              {/* SET PARTNER CUSTOM COMMISSION AT APPROVAL TIME */}
              {reviewAction === 'APPROVE' && (
                <div className="p-3 bg-purple-50/70 border border-purple-200 rounded-2xl space-y-2">
                  <span className="text-[11px] font-extrabold text-purple-900 uppercase block">Set Partner Commission Rate</span>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Type</label>
                      <select
                        value={partnerCommType}
                        onChange={e => setPartnerCommType(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-xl p-2 text-xs font-bold text-slate-900 outline-none"
                      >
                        <option value="PERCENTAGE">Percentage (%)</option>
                        <option value="FIXED">Fixed (₹)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Rate / Value</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={partnerCommVal}
                        onChange={e => setPartnerCommVal(e.target.value)}
                        placeholder="0 for 0%"
                        className="w-full bg-white border border-slate-300 rounded-xl p-2 text-xs font-bold text-slate-900 outline-none"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-purple-700 font-medium">Set to 0 if this partner has 0% platform commission fee.</p>
                </div>
              )}

              {['REJECT', 'REQUEST_RESUBMISSION', 'SUSPEND'].includes(reviewAction) && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Reason / Note for Partner</label>
                  <textarea
                    rows={3}
                    required
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Specify exact reason (e.g., PAN name mismatch or invalid IFSC)..."
                    className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-3 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">
                Cancel
              </button>
              <button
                onClick={handleReviewSubmit}
                disabled={actionLoading}
                className="px-5 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-extrabold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm"
              >
                {actionLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiCheck className="w-4 h-4" />}
                <span>Submit Review Action</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. STYLED PARTNER COMMISSION EDIT MODAL */}
      {modal === 'editPartnerComm' && selectedApp && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-md space-y-4 text-left shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-sm font-black text-slate-900">Set Partner Commission Rate</h3>
                <p className="text-[11px] font-semibold text-purple-900">{selectedApp.legalName} ({selectedApp.organizationName || 'Individual'})</p>
              </div>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <form onSubmit={handleSavePartnerCommDirectly} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Commission Type</label>
                <select
                  value={partnerCommType}
                  onChange={e => setPartnerCommType(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED">Fixed Amount (₹)</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Commission Value ({partnerCommType === 'PERCENTAGE' ? '%' : '₹'})
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={partnerCommVal}
                  onChange={e => setPartnerCommVal(e.target.value)}
                  placeholder="0 for 0% commission"
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                />
                <p className="text-[10px] text-purple-700 mt-1 font-medium">Setting 0 will remove platform fee deduction for this partner.</p>
              </div>

              <div className="flex gap-3 justify-end pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingComm}
                  className="px-5 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-extrabold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm"
                >
                  {savingComm ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiCheck className="w-4 h-4" />}
                  <span>Save Commission</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerVerifications;
